import Cookies from "js-cookie";
import { getApiBase } from "./apiBase";
import { currentLoginPath, isLoginPath } from "./loginRedirect";

interface RequestOptions extends RequestInit {
  headers?: Record<string, string>;
  body?: any;
  /** "arrayBuffer" resolves with the raw body (binary endpoints, e.g. frame packs). */
  responseType?: "json" | "arrayBuffer";
}

// Global auth state management
let authState: {
  logout: () => void;
  navigate: (path: string) => void;
} | null = null;
let isHandlingSessionExpiration = false;

// Function to set auth state (called from AuthContext)
export const setAuthState = (
  logout: () => void,
  navigate: (path: string) => void
) => {
  authState = { logout, navigate };
};

export const api = {
  request: async <T>(
    url: string,
    {
      method = "GET",
      body = null,
      headers = {},
      responseType = "json",
      ...options
    }: RequestOptions = {}
  ): Promise<T> => {
    let requestHeaders: Record<string, string> = {};
    let requestBody = body;

    // Only set default Content-Type if not FormData
    if (!(body instanceof FormData)) {
      requestHeaders["Content-Type"] = "application/json";
      requestBody = body && method !== "GET" ? JSON.stringify(body) : undefined;
    }

    if (
      method === "POST" ||
      method === "PUT" ||
      method === "PATCH" ||
      method === "DELETE"
    ) {
      const csrfToken = Cookies.get("csrftoken") || "";
      requestHeaders["X-CSRFToken"] = csrfToken;
    }

    try {
      const response = await fetch(`${getApiBase()}${url}`, {
        method,
        body: requestBody,
        headers: {
          ...requestHeaders,
          ...headers,
        },
        credentials: "include",
        ...options,
      });

      if (!response.ok) {
        // Session expiration (401 Unauthorized): clear auth state and return
        // to login. A 403 is a permission error on a live session — it falls
        // through to the normal error path below so callers can surface the
        // server's message instead of the user being logged out (issue #200).
        // Public story pages (/s/<slug>) work without a session — never
        // redirect an anonymous viewer there to the login page.
        // The login page itself probes /api/me; a 401 there is expected and
        // must not re-navigate (it would drop ?next= and ?sso_error=).
        const onPublicRoute =
          typeof window !== "undefined" &&
          (window.location.pathname.startsWith("/s/") ||
            isLoginPath(window.location.pathname));

        if (response.status === 401 && !isHandlingSessionExpiration && !onPublicRoute) {
          isHandlingSessionExpiration = true;

          // Clear any stored auth data
          Cookies.remove("csrftoken");

          // Trigger logout and redirect if auth state is available
          if (authState) {
            try {
              authState.logout();
              authState.navigate(currentLoginPath());
            } catch (error) {
              console.error("Error during session expiration handling:", error);
            }
          }

          // Reset the flag after a short delay to allow for future session checks
          setTimeout(() => {
            isHandlingSessionExpiration = false;
          }, 1000);

          throw {
            message: "Session expired. Please log in again.",
            status: response.status,
            data: { message: "Session expired" },
          };
        }

        const errorData = await response.json().catch(() => ({}));
        throw {
          // DRF-style views return "detail"; ours mostly return "message".
          // Without the fallback a real explanation shows as "Request failed".
          message: errorData.message || errorData.detail || "Request failed",
          status: response.status,
          data: errorData,
        };
      }

      if (responseType === "arrayBuffer") {
        return (await response.arrayBuffer()) as T;
      }

      // Check if response has content before trying to parse JSON
      const contentType = response.headers.get("content-type");
      const contentLength = response.headers.get("content-length");

      if (
        contentLength === "0" ||
        response.status === 204 ||
        !contentType?.includes("application/json")
      ) {
        return null as T;
      }

      return response.json();
    } catch (error) {
      if (error instanceof Error) {
        throw {
          message: error.message,
          status: undefined,
          data: {},
        };
      }
      throw error;
    }
  },

  get: async <T>(url: string, options?: RequestOptions): Promise<T> => {
    return api.request<T>(url, { ...options, method: "GET" });
  },

  post: async <T>(
    url: string,
    data?: any,
    options?: RequestOptions
  ): Promise<T> => {
    return api.request<T>(url, { ...options, method: "POST", body: data });
  },

  put: async <T>(
    url: string,
    data?: any,
    options?: RequestOptions
  ): Promise<T> => {
    return api.request<T>(url, { ...options, method: "PUT", body: data });
  },

  patch: async <T>(
    url: string,
    data?: any,
    options?: RequestOptions
  ): Promise<T> => {
    return api.request<T>(url, { ...options, method: "PATCH", body: data });
  },

  delete: async <T>(url: string, options?: RequestOptions): Promise<T> => {
    return api.request<T>(url, { ...options, method: "DELETE" });
  },
};
